<script setup>
  import {computed, inject, reactive, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentAutocomplete from "@components/Autocomplete/Autocomplete.vue";
  import ComponentPatientGenderDiverseDialog from "@components/Dialogs/PatientGenderDiverseDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentBirthdate from "@components/Inputs/Birthdate.vue";
  import ComponentInput from "@components/Inputs/Input.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm} from "@inertiajs/vue3";
  import {patientGender} from "@pages/Patients/Enums/Enums.js";
  import {MedButton, MedIcon, MedLink, MedSpinner, MedTooltip} from "@pharma4u/medicheck-components";
  import filterExistingPatients from "@utils/Helpers/FilterExistingPatients.js";
  import {debounce} from "es-toolkit";

  import {useAuthHttp} from "@/resources/js/Composable/useAuthHttp.js";

  import DashboardWidget from "./Widget.vue";

  defineOptions({
    name: "DashboardQuickStartExclusiveWidget",
  });

  let activeSearch = null;

  const privacy = inject("$privacy");

  const refLastnameAutocomplete = ref(null);
  const refDiverseDialog = ref(null);

  const showFullForm = ref(false);
  const autocompleteInput = ref(null);
  const existingPatients = ref([]);

  const form = useForm({
    patient_id: null,
    lastname: null,
    firstname: null,
    birthdate: null,
    insurancenumber: null,
    gender: null,
  });

  // separate clipped values from form, as they would
  // always falsely trigger form.isDirty state
  const clippedValues = reactive({
    firstname_clipped: null,
    lastname_clipped: null,
    birthdate_clipped: null,
    insurancenumber_clipped: null,
  });

  const isValid = computed(() => {
    return form.firstname !== null && form.lastname !== null && form.birthdate !== null && form.gender !== null;
  });

  watch(autocompleteInput, () => {
    // autocompleted lastname is not clipped yet...
    clip("lastname", autocompleteInput.value);

    // autocompleted lastname is not encrypted yet...
    privacy.encryptValue(autocompleteInput.value).then((encryptedLastname) => {
      form.lastname = encryptedLastname;
    });
  });

  watch(
    () => form.gender,
    () => {
      if (form.gender === "diverse") {
        refDiverseDialog.value.open();
      }
      searchExistingPatients();
    },
  );

  watch(
    clippedValues,
    debounce(() => {
      searchExistingPatients();
    }, 500),
  );

  function clip(field, value) {
    switch (field) {
      case "birthdate":
        if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(value)) {
          // YYYY-MM-DD
          clippedValues["birthdate_clipped"] = value.split("-")[0] ?? null;
        } else if (/^\d{1,2}.\d{1,2}.\d{4}$/.test(value)) {
          clippedValues["birthdate_clipped"] = value.split(".")[2] ?? null;
        } else {
          clippedValues["birthdate_clipped"] = null;
        }
        break;
      case "firstname":
      case "suffix":
      case "prefix":
      case "lastname":
      case "insurancenumber":
        clippedValues[field + "_clipped"] = (value ?? "").slice(0, 3) || null;
        break;
    }
  }

  /**
   * Search for existing patients when typing in "lastname" autocomplete field
   * @param searchTerm
   * @returns {Promise<unknown>}
   */
  function autocompleteLastname(searchTerm) {
    return new Promise((resolve) => {
      useAuthHttp({lastname_clipped: searchTerm}).post(route("api.patients.search"), {
        onSuccess: (response) => {
          Promise.all(response?.data?.map(async (patient) => await privacy.decryptPatient(patient))).then(
            (foundPatients) => {
              if (foundPatients.length > 0) {
                let searchResult = filterExistingPatients(
                  {
                    lastname: autocompleteInput.value,
                  },
                  foundPatients,
                  ["lastname"],
                  true,
                ).map((patient) => {
                  let d = new Date(patient.birthdate ?? null);
                  patient.birthdate = Number.isNaN(d.getTime())
                    ? null
                    : d.toLocaleDateString("de-DE", {dateStyle: "medium"});
                  return patient;
                });
                resolve(searchResult);
              } else {
                resolve([]);
              }
            },
          );
        },
      });
    });
  }

  function searchExistingPatients() {
    // early exit if required fields are not set
    if (!clippedValues.firstname_clipped || !clippedValues.lastname_clipped) {
      return;
    }

    if (activeSearch) {
      activeSearch.cancel();
    }

    const search = useAuthHttp({...clippedValues});
    activeSearch = search;

    search.post(route("api.patients.search"), {
      onSuccess: (response) => {
        Promise.all(response?.data?.map(async (patient) => await privacy.decryptPatient(patient))).then(
          (foundPatients) => {
            if (foundPatients.length > 0) {
              privacy.decryptPatient(form.data()).then((currentPatient) => {
                let compareAgainst = ["firstname", "lastname"];
                if (currentPatient.gender !== null) {
                  compareAgainst.push("gender");
                }
                if (currentPatient.birthdate !== null) {
                  compareAgainst.push("birthdate");
                }
                if (currentPatient.insurancenumber !== null) {
                  compareAgainst.push("insurancenumber");
                }

                existingPatients.value = filterExistingPatients(currentPatient, foundPatients, compareAgainst).map(
                  (patient) => {
                    let d = new Date(patient.birthdate ?? null);
                    patient.birthdate = Number.isNaN(d.getTime())
                      ? null
                      : d.toLocaleDateString("de-DE", {dateStyle: "medium"});
                    return patient;
                  },
                );
              });
            }
          },
        );
      },
    });
  }

  function startWithExistingPatient(selectedPatient) {
    refLastnameAutocomplete.value.reset();

    form
      // reject everything but "patient_id"
      .transform(() => ({
        patient_id: selectedPatient.id,
      }))
      .post(route("dashboard.quickstart"));
  }

  function startWithNewPatient() {
    if (!isValid.value) {
      return false;
    }

    form
      .transform((data) => ({
        ...data,
        ...clippedValues,
      }))
      .post(route("dashboard.quickstart"));
  }

  function createNewPatient() {
    existingPatients.value = [];
    form.patient_id = null;
    form.firstname = null;
    form.birthdate = null;
    form.gender = null;
    form.insurancenumber = null;
    showFullForm.value = true;
  }

  function cancel() {
    createNewPatient();
    refLastnameAutocomplete.value.reset();
    showFullForm.value = false;
    autocompleteInput.value = null;
  }
</script>

<template>
  <!-- TODO fix that shit - absolute in fk grid? -->
  <DashboardWidget
    :hide-actions="!showFullForm"
    :class="{'min-h-48': !showFullForm, 'h-auto! shadow-2xl!': showFullForm}"
    :test-id="getTestId('quick-start-exclusive')"
  >
    <template #title>
      <div class="flex items-start justify-between gap-2">
        <MedButton
          flat
          full-width
          size="lg"
          align="left"
          class="text-2xl"
          :data-test="getTestId('quick-start-exclusive-widget-show-full-form-button')"
          @click.prevent="showFullForm = !showFullForm"
        >
          <span class="flex rounded-full bg-mcgreen p-1.5 font-normal text-white">
            <MedIcon icon="add" size="lg" />
          </span>

          Neue Analyse
        </MedButton>
      </div>
    </template>

    <template #default>
      <div v-if="form.processing" class="text-center">
        <MedSpinner size="3xl" :data-test="getTestId('quick-start-exclusive-widget-spinner')" />
      </div>

      <div v-else>
        <p v-if="!showFullForm" class="col-span-2 pb-4 text-lg">Starten Sie eine neue Analyse manuell</p>

        <form
          class="grid grid-cols-2 gap-4"
          :data-test="getTestId('quick-start-exclusive-widget-form')"
          @submit.prevent="startWithNewPatient()"
        >
          <!-- TODO: Popover prevents tooltip overlay - replace with custom dropdown later -->
          <ComponentAutocomplete
            ref="refLastnameAutocomplete"
            v-model="autocompleteInput"
            class="col-span-2"
            :label="showFullForm ? 'Nachname*' : ''"
            :fetch-method="autocompleteLastname"
            key-name="lastname"
            :clearable="!showFullForm"
            :placeholder="showFullForm ? '' : 'Nachname suchen oder neu eingeben'"
            :min-search-term-length="2"
            :test-id="getTestId('quick-start-exclusive-widget-form-lastname')"
            @selected="startWithExistingPatient"
          >
            <template #autocomplete-item="{item}">
              <div class="flex min-h-10 min-w-56 items-center justify-between space-x-2">
                <div>
                  <p v-if="item.suffix || item.prefix" class="text-sm leading-tight text-gray-500">
                    {{ item.suffix }} {{ item.prefix }}
                  </p>

                  <div class="font-semibold">{{ item.lastname }} {{ item.firstname }}</div>

                  <p class="text-sm leading-tight text-gray-500">
                    {{ [item.birthdate, item.insurancenumber].filter((i) => i !== null).join(", ") }}
                  </p>
                </div>

                <div>
                  <MedTooltip content="Neue Medikationsanalyse starten">
                    <ComponentIcon class="rounded-full bg-mcgreen p-1 text-white">add</ComponentIcon>
                  </MedTooltip>
                </div>
              </div>
            </template>

            <template #autocomplete-prepend>
              <div v-if="!showFullForm" class="flex items-center space-x-3 p-3 text-sm text-gray-500">
                <MedLink
                  as="button"
                  variant="primary"
                  :data-test="getTestId('quick-start-exclusive-widget-form-autocomplete-dropdown-create-patient-link')"
                  @click.prevent="createNewPatient()"
                >
                  als neuen Patienten anlegen
                </MedLink>
              </div>
            </template>
          </ComponentAutocomplete>

          <ComponentInput
            v-if="showFullForm"
            v-model="form.firstname"
            class="col-span-2"
            label="Vorname*"
            :hide-clear="true"
            :encrypted="true"
            :validation="form.errors.firstname"
            :test-id="getTestId('quick-start-exclusive-widget-form-firstname')"
            @decrypted.once="clip('firstname', $event)"
            @keyup="clip('firstname', $event.target.value)"
          />

          <ComponentBirthdate
            v-if="showFullForm"
            v-model="form.birthdate"
            class="col-span-1"
            label="Geburtsdatum*"
            :validation="form.errors.birthdate"
            :test-id="getTestId('quick-start-exclusive-widget-form-birthdate')"
            @decrypted.once="clip('birthdate', $event)"
            @blur="clip('birthdate', $event.target.value)"
          />

          <ComponentSelect
            v-if="showFullForm"
            v-model="form.gender"
            class="col-span-1"
            label="Geschlecht*"
            :validation="form.errors.gender"
            :options="patientGender"
            :test-id="getTestId('quick-start-exclusive-widget-form-gender')"
          />

          <ComponentInput
            v-if="showFullForm"
            v-model="form.insurancenumber"
            class="col-span-1"
            label="Versichertennummer"
            :hide-clear="true"
            :encrypted="true"
            :validation="form.errors.insurancenumber"
            :test-id="getTestId('quick-start-exclusive-widget-form-insurance-number')"
            @decrypted.once="clip('insurancenumber', $event)"
            @keyup="clip('insurancenumber', $event.target.value)"
          />
        </form>

        <p v-if="showFullForm" class="mt-2 text-right text-sm">* Pflichtangaben</p>

        <ComponentAlert
          v-if="existingPatients.length > 0"
          class="mt-2 mb-0"
          type="error"
          :allow-closing="false"
          :test-id="getTestId('quick-start-exclusive-widget-patient-exists')"
        >
          <h2 class="text-lg">Eventuell existiert dieser Patient bereits?</h2>

          <p class="mt-2 text-sm">
            Es
            <template v-if="existingPatients.length === 1">existiert bereits ein Patient</template>
            <template v-if="existingPatients.length > 1">existieren bereits mehrere Patienten</template>
            mit gleichen Angaben.
            <br />
            Nutzen Sie ggf. die Suche im Feld „Nachname“ und vermeiden Sie, eine Person doppelt anzulegen.
          </p>
          <p class="mt-2 text-sm">Wenn Sie eine Dublette ausschließen, können Sie diesen Hinweis ignorieren.</p>
        </ComponentAlert>
      </div>

      <ComponentPatientGenderDiverseDialog ref="refDiverseDialog" />
    </template>

    <template #actions>
      <MedButton
        :disabled="!isValid"
        :is-loading="form.processing"
        variant="secondary"
        :data-test="getTestId('quick-start-exclusive-widget-create-patient-button')"
        @click="startWithNewPatient()"
      >
        Starten
      </MedButton>

      <MedButton flat :data-test="getTestId('quick-start-exclusive-widget-cancel-button')" @click="cancel()">
        Abbrechen
      </MedButton>
    </template>
  </DashboardWidget>
</template>
